import React, { useCallback } from "react";
import styled from "styled-components";
import Immutable from "immutable";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { Button, Input } from "components/bootstrap";
import { Icon, SortableList } from "components/common";
import TimeRangeFilter from "views/components/searchbar/time-range-filter";
import TimeRangeInputSettingsContext from "views/components/contexts/TimeRangeInputSettingsContext";
import generateId from "logic/generateId";
const ItemWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 5px;
  flex-grow: 1;
`;
const StyledInput = styled(Input)`
  margin-bottom: 0;
  width: 200px;
`;
const IconWrapper = styled.div`
  width: 40px;
`;
const Description = styled.div`
  display: flex;
`;
const contextSettings = {
  showDropdownButton: false,
  showPresetsButton: false,
  showAddToQuickListButton: false
};
const TimeRangePresetFormItem = ({ idx, id, timerange, description, onChange, onRemove, limitDuration }) => {
  const { t } = useTranslation("configurations");
  const handleOnChangeRange = useCallback(
    (newTimerange) => {
      onChange({ timerange: newTimerange, description, id }, idx);
    },
    [description, id, idx, onChange]
  );
  const handleOnChangeDescription = useCallback(
    (newDescription) => {
      onChange({ timerange, description: newDescription, id }, idx);
    },
    [id, idx, onChange, timerange]
  );
  const handleOnRemove = useCallback(() => {
    onRemove(idx);
  }, [idx, onRemove]);
  const debounceHandleOnChangeDescription = debounce((value) => handleOnChangeDescription(value), 300);
  return /* @__PURE__ */ React.createElement(ItemWrapper, { "data-testid": `time-range-preset-${id}` }, /* @__PURE__ */ React.createElement(
    TimeRangeFilter,
    {
      onChange: handleOnChangeRange,
      limitDuration,
      value: timerange,
      withinPortal: false
    }
  ), /* @__PURE__ */ React.createElement(Description, null, /* @__PURE__ */ React.createElement(
    StyledInput,
    {
      type: "text",
      id: `quick-access-time-range-description-${id}`,
      placeholder: t("time_range_presets.add_description"),
      title: t("time_range_presets.description_label"),
      "aria-label": t("time_range_presets.description_label"),
      required: true,
      defaultValue: description,
      onChange: ({ target: { value } }) => debounceHandleOnChangeDescription(value),
      formGroupClassName: ""
    }
  ), /* @__PURE__ */ React.createElement(IconWrapper, { className: "input-group-addon", onClick: handleOnRemove, title: t("time_range_presets.remove") }, /* @__PURE__ */ React.createElement(Icon, { name: "delete", style: { cursor: "pointer" } }))));
};
const TimeRangePresetForm = ({
  options,
  onUpdate
}) => {
  const { t } = useTranslation("configurations");
  const onChange = useCallback(
    (newPreset, idx) => {
      const newState = options.set(idx, newPreset);
      onUpdate(newState);
    },
    [onUpdate, options]
  );
  const onRemove = useCallback(
    (idx) => {
      const newState = options.delete(idx);
      onUpdate(newState);
    },
    [onUpdate, options]
  );
  const onMoveItem = useCallback(
    (items) => {
      onUpdate(Immutable.List(items));
    },
    [onUpdate]
  );
  const addTimeRange = useCallback(() => {
    onUpdate(
      options.push({
        id: generateId(),
        description: "",
        timerange: { type: "relative", from: 300 }
      })
    );
  }, [onUpdate, options]);
  const customContentRender = useCallback(
    ({ item: { id, description, timerange }, index }) => /* @__PURE__ */ React.createElement(
      TimeRangePresetFormItem,
      {
        id,
        onRemove,
        idx: index,
        onChange,
        timerange,
        description,
        limitDuration: 0
      }
    ),
    [onChange, onRemove]
  );
  return /* @__PURE__ */ React.createElement("div", { className: "form-group" }, /* @__PURE__ */ React.createElement("strong", null, t("searches.time_range_presets")), /* @__PURE__ */ React.createElement("span", { className: "help-block" }, /* @__PURE__ */ React.createElement("span", null, t("time_range_presets.help"))), /* @__PURE__ */ React.createElement("div", { className: "wrapper" }, /* @__PURE__ */ React.createElement(TimeRangeInputSettingsContext.Provider, { value: contextSettings }, /* @__PURE__ */ React.createElement(
    SortableList,
    {
      items: options.toArray(),
      onMoveItem,
      displayOverlayInPortal: true,
      alignItemContent: "center",
      customContentRender
    }
  ))), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsSize: "xs",
      onClick: addTimeRange,
      title: t("time_range_presets.add_new"),
      "aria-label": t("time_range_presets.add_new")
    },
    t("time_range_options.add_option")
  ));
};
export default TimeRangePresetForm;
